/*
 * Control Service
 * Only used to control category and its navigation
 * Use Control helper service for helper tasks
 * It uses selectCategory() to perform action based on category
 * Action list is defined in BO actio Type
 * Please refer to lobby interface for structure of category
 * Only make changes if related to category control
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { ConfigService } from '../config/config.service';
import { CategoryInterface, GameInterface, SubCategoryInterface } from 'src/app/interfaces/lobby.interface';
import { RoutingService } from '../routing/routing.service';
import { AppLinksService } from '../applinks/app-links.service';
import { ControlHelperService } from './control-helper.service';
import { switchMap, take } from 'rxjs/operators';
import { FavoritesService } from '../favorite/favorite.service';
import { LastPlayedService } from '../lastplayed/lastplayed.service';
import { StructureHelperService } from '../structure/structure-helper.service';

@Injectable({
  providedIn: 'root'
})
export class ControlService {

  private selectedCategorySubject = new BehaviorSubject<CategoryInterface | null>(null);
  public selectedCategory$: Observable<CategoryInterface | null> = this.selectedCategorySubject.asObservable();
  public selectedCategoryGames$: Observable<GameInterface[]> =
    combineLatest([
      this.selectedCategory$,
      this.lastPlayedService.lastPlayed$,
      this.favoritesService.favorites$
    ]).pipe(
      switchMap(([category, lg, fg]) => {
        if (category?.action_type === 'lastplayed') {
          return of(lg);
        }
        if (category?.action_type === 'favorite') {
          return of(fg);
        }
        return of(category?.games || []);
      })
    );

  private selectedSubCategorySubject = new BehaviorSubject<CategoryInterface | null>(null);
  public selectedSubCategory$: Observable<CategoryInterface | null> = this.selectedSubCategorySubject.asObservable();

  public selectedSubCategoryGames$: Observable<GameInterface[]> =
    combineLatest([
      this.selectedSubCategory$,
      this.lastPlayedService.lastPlayed$,
      this.favoritesService.favorites$
    ]).pipe(
      switchMap(([subcategory, lg, fg]) => {
        if (!subcategory) return of([]);
        switch (subcategory?.action_type) {
          case 'lastplayed':
            return of(lg);
          case 'favorite':
            return of(fg);
          default:
            return of(subcategory?.games || []);
        }
      })
    );

  public depositFlow: boolean = false;

  constructor(
    private appLinks: AppLinksService,
    private favoritesService: FavoritesService,
    private helper: ControlHelperService,
    private lastPlayedService: LastPlayedService,
    private routing: RoutingService,
    private structureHelper: StructureHelperService,
  ) { }

  //Please Read Comments at Top

  public selectCategory(category: CategoryInterface): void {
    if (!category) {
      this.selectedCategorySubject.next(null)
      this.selectedSubCategorySubject.next(null)
      return console.warn('[ControlService: SelectCategory]: Invalid category received.');
    }

    const actionType = category?.action_type;
    const route = category?.category_route;

    if (actionType === 'lastplayed') {
      this.lastPlayedService.refreshLastPlayed();
    }

    const select = () => {
      this.selectedCategorySubject.next(category);
      this.selectedSubCategorySubject.next(null);
      this.helper.setLastActiveLobbyCategory(category);
    }

    switch (actionType) {
      case 'gamelist':
      case 'lastplayed':
      case 'favorite':
        this.routing.toLobbyTab(route);
        select();
        break;
      case 'home':
        this.goToDefaultCategory();
        break;
      case 'externalpageload':
        this.appLinks.openExternalPage(route);
        break;
      case 'dialogue':
        this.helper.controlNavigateTo(route);
        break;
      case 'promotions':
        this.routing.toPromotionsTab();
        select();
        break;
      case 'article':
        this.helper.controlOpenWebview(route);
        this.routing.toWebviewTab(true, route);
        select();
        break;
      default:
        this.helper.onAlert(category);
        console.warn(`[ControlService: SelectCategory]: Unrecognized actionType "${actionType}"`);
        this.routing.defaultRoute();
        break;
    }
  }

  //Please Read Comments at Top

  public selectSubCategory(category: CategoryInterface, disableRouting = false): void {
    if (!disableRouting) {
      const route = category?.category_route;
      this.routing.toLobbyTab(route);
    }
    this.selectedSubCategorySubject.next(category);
  }

  public onSeeAll(laneData: SubCategoryInterface): void {
    this.selectSubCategory(laneData);
  }

  public goToDefaultCategory(): any {

    this.structureHelper.getDefaultCategory().pipe(take(1)).subscribe((category: CategoryInterface) => {
      if (!category) return console.warn('[goToDefaultCategory] No default category found');

      this.selectCategory(category);
      this.helper.setActiveLobbyCategory(category);
      this.selectSubCategory(null, !!category);
    });
  }

  public setCategoryForWordPressPage(): void {
    this.selectCategory(null);
    this.helper.setActiveLobbyCategory(null);
  }

  public setPromotionPage(): void {
    this.structureHelper.getPromotionCategory().pipe(take(1)).subscribe((category: CategoryInterface) => {
      if (!category) return console.warn('[setPromotionPage] No default category found');

      this.selectCategory(category);
      this.helper.setActiveLobbyCategory(category);
      this.selectSubCategory(null, !!category); // check for category to avoid aditional router navigate call
    })
  }
}

//Please Read Comments at Top
//todo LIve Dealer confirm popup
//todo promotion tab
//todo load livedelar after geo
//launch freespin
//todo show no free spin alert
